import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MoreHorizontal, Pin, Search, X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { ChatActionsMenu } from './ChatActionsMenu';
import type { ChatPinPatch } from './ChatHistorySidebar';

const SEARCH_DEBOUNCE_MS = 300;

export type ChatSearchRecent = {
  id: string;
  title: string;
  created_at?: string;
  updated_at: string;
  pinned_at?: string | null;
};

type ChatSearchHit = {
  chatId: string;
  title: string;
  snippet: string;
  matchedIn: 'title' | 'message';
  messageCreatedAt: string | null;
  chatUpdatedAt: string | null;
  rank: number;
  pinnedAt: string | null;
};

function formatGeminiDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const isCurrentYear = d.getFullYear() === now.getFullYear();

  const day = d.getDate();
  const months = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
  const monthStr = months[d.getMonth()] ?? '';

  if (isCurrentYear) {
    return `${day}. ${monthStr}`;
  }
  return `${day}. ${monthStr} ${d.getFullYear()}`;
}

function HeadlineSnippet({ text }: { text: string }) {
  const segments = text.split(/(«|»)/);
  let highlight = false;
  return (
    <>
      {segments.map((segment, i) => {
        if (segment === '«') {
          highlight = true;
          return null;
        }
        if (segment === '»') {
          highlight = false;
          return null;
        }
        if (!segment) return null;
        return highlight ? (
          <mark key={i} className="bg-amber-100 text-inherit font-medium rounded-[2px] px-0.5">
            {segment}
          </mark>
        ) : (
          <span key={i}>{segment}</span>
        );
      })}
    </>
  );
}

interface ChatSearchViewProps {
  onSelectChat: (id: string) => void;
  recentChats: ChatSearchRecent[];
  currentChatId: string | null;
  onTogglePin?: (chatId: string, pinned: boolean, previousPinnedAt: string | null) => void;
  onDeleteChat?: (chatId: string) => void;
  pinPatch?: ChatPinPatch | null;
}

function SearchChatRow({
  title,
  snippet,
  dateStr,
  isActive,
  isPinned,
  onSelect,
  onTogglePin,
  onDelete,
}: {
  title: string;
  snippet?: string;
  dateStr: string;
  isActive: boolean;
  isPinned: boolean;
  onSelect: () => void;
  onTogglePin?: () => void;
  onDelete?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      className={`group relative flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
        isActive ? 'bg-blue-50 text-blue-700' : `text-app-text hover:bg-app-hover/70 ${menuOpen ? 'bg-app-hover/70' : ''}`
      }`}
    >
      {isPinned ? (
        <Pin
          className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isActive ? 'text-blue-500' : 'text-slate-400'}`}
          aria-hidden
        />
      ) : null}
      <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left">
        <span className="block truncate text-[14px] font-normal leading-5">{title}</span>
        {snippet ? (
          <span className={`mt-0.5 block line-clamp-1 text-xs font-normal ${isActive ? 'text-blue-500' : 'text-app-text-subtle'}`}>
            <HeadlineSnippet text={snippet} />
          </span>
        ) : null}
      </button>
      {dateStr ? (
        <span className="shrink-0 self-start mt-0.5 pr-8 text-xs font-normal text-slate-400">{dateStr}</span>
      ) : null}
      <ChatActionsMenu
        align="right"
        isPinned={isPinned}
        onOpenChange={setMenuOpen}
        onRename={onSelect}
        onDelete={() => onDelete?.()}
        onTogglePin={onTogglePin}
        triggerClassName={`absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md transition-opacity focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-pipedrive-blue ${
          isActive
            ? 'bg-blue-50 text-blue-500 hover:bg-blue-100'
            : 'bg-app-surface text-slate-400 hover:bg-app-hover hover:text-app-text-muted'
        } ${menuOpen ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100'}`}
        renderTrigger={() => <MoreHorizontal className="h-3.5 w-3.5" />}
      />
    </div>
  );
}

export function ChatSearchView({
  onSelectChat,
  recentChats,
  currentChatId,
  onTogglePin,
  onDeleteChat,
  pinPatch,
}: ChatSearchViewProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchHits, setSearchHits] = useState<ChatSearchHit[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useLayoutEffect(() => {
    if (!pinPatch) return;
    setSearchHits((prev) =>
      prev.map((hit) => (hit.chatId === pinPatch.chatId ? { ...hit, pinnedAt: pinPatch.pinnedAt } : hit)),
    );
  }, [pinPatch]);

  useEffect(() => {
    searchAbortRef.current?.abort();
    if (!debouncedQuery) {
      setSearchHits([]);
      setSearchLoading(false);
      searchAbortRef.current = null;
      return;
    }
    const ac = new AbortController();
    searchAbortRef.current = ac;
    setSearchLoading(true);
    void (async () => {
      try {
        const res = await fetch(
          `/api/ai/chats/search?q=${encodeURIComponent(debouncedQuery)}&limit=30`,
          { credentials: 'include', signal: ac.signal },
        );
        if (!res.ok) {
          if (!ac.signal.aborted) {
            setSearchHits([]);
            setSearchLoading(false);
          }
          return;
        }
        const data: unknown = await res.json();
        if (ac.signal.aborted) return;
        const rows = Array.isArray(data) ? data : [];
        const hits: ChatSearchHit[] = [];
        for (const row of rows) {
          if (!row || typeof row !== 'object') continue;
          const rec = row as Record<string, unknown>;
          if (typeof rec.chatId !== 'string' || typeof rec.title !== 'string') continue;
          hits.push({
            chatId: rec.chatId,
            title: rec.title,
            snippet: typeof rec.snippet === 'string' ? rec.snippet : '',
            matchedIn: rec.matchedIn === 'title' ? 'title' : 'message',
            messageCreatedAt: typeof rec.messageCreatedAt === 'string' ? rec.messageCreatedAt : null,
            chatUpdatedAt: typeof rec.chatUpdatedAt === 'string' ? rec.chatUpdatedAt : null,
            rank: typeof rec.rank === 'number' ? rec.rank : 0,
            pinnedAt: typeof rec.pinnedAt === 'string' ? rec.pinnedAt : null,
          });
        }
        setSearchHits(hits);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        if (!ac.signal.aborted) setSearchHits([]);
      } finally {
        if (!ac.signal.aborted) setSearchLoading(false);
      }
    })();
    return () => ac.abort();
  }, [debouncedQuery]);

  const searching = debouncedQuery.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      {/* Suchfeld und Überschrift stehen fest — nur die Trefferliste scrollt (wie bei Gemini). */}
      <div className="shrink-0 px-4 md:px-8 pt-8 md:pt-12">
        {/* Gemini Style Large Search Input Box */}
        <div className="relative mx-auto w-full max-w-[720px]">
          <div className="relative flex items-center w-full rounded-2xl bg-app-surface border border-app-border shadow-sm">
            <Search className="h-5 w-5 text-slate-400 ml-4 shrink-0 pointer-events-none" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              name="gemini-chat-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Chats durchsuchen"
              className="w-full bg-transparent border-0 outline-none text-[15px] text-app-text placeholder:text-slate-400 py-3.5 pl-3 pr-10 rounded-2xl"
              aria-label="Chats durchsuchen"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  inputRef.current?.focus();
                }}
                className="absolute right-3 p-1 rounded-full text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
                aria-label="Eingabe löschen"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="mx-auto w-full max-w-[720px] px-2 pt-8 pb-3">
          <h3 className="text-xs font-normal text-slate-400">
            {searching ? 'Ergebnisse' : 'Neueste'}
          </h3>
        </div>
      </div>

      <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 md:px-8 pb-8">
        <div className="mx-auto w-full max-w-[720px]">
          {searching ? (
            searchLoading ? (
              <div className="py-12 flex justify-center">
                <InlineLoadingState label="Chats werden gesucht …" />
              </div>
            ) : searchHits.length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-slate-400">Keine Chats gefunden.</p>
            ) : (
              <div className="space-y-0.5">
                {searchHits.map((hit) => (
                  <SearchChatRow
                    key={hit.chatId}
                    title={hit.title}
                    snippet={hit.snippet}
                    dateStr={formatGeminiDate(hit.messageCreatedAt || hit.chatUpdatedAt)}
                    isActive={currentChatId === hit.chatId}
                    isPinned={Boolean(hit.pinnedAt)}
                    onSelect={() => onSelectChat(hit.chatId)}
                    onTogglePin={
                      onTogglePin
                        ? () => onTogglePin(hit.chatId, !hit.pinnedAt, hit.pinnedAt)
                        : undefined
                    }
                    onDelete={onDeleteChat ? () => onDeleteChat(hit.chatId) : undefined}
                  />
                ))}
              </div>
            )
          ) : (
            recentChats.length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-slate-400">Noch keine Chats vorhanden.</p>
            ) : (
              <div className="space-y-0.5">
                {recentChats.map((chat) => (
                  <SearchChatRow
                    key={chat.id}
                    title={chat.title}
                    dateStr={formatGeminiDate(chat.updated_at || chat.created_at)}
                    isActive={currentChatId === chat.id}
                    isPinned={Boolean(chat.pinned_at)}
                    onSelect={() => onSelectChat(chat.id)}
                    onTogglePin={
                      onTogglePin
                        ? () => onTogglePin(chat.id, !chat.pinned_at, chat.pinned_at ?? null)
                        : undefined
                    }
                    onDelete={onDeleteChat ? () => onDeleteChat(chat.id) : undefined}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
